'use client';

import { CategoryDocument } from '@models/category';
import Breadcrumb from '@ui/Breadcrumb';
import Button from '@ui/Button';
import { getGenderLabel } from '@util/createListingHelpers';
import { PageSideFilters } from '@util/hooks/useProductsPage';
import { capitalize, getBreadcrumbItems } from '@util/index';
import { useAuth } from 'context/AuthContext';

type CategoryPageHeaderProps = {
  current_cat: CategoryDocument | null;
  searchTitle: string | null;
  // categories: CategoryDocument[];
  filterState: PageSideFilters;
};

const CategoryPageHeader = ({
  current_cat,
  searchTitle,
  filterState,
}: CategoryPageHeaderProps) => {
  const { userDoc } = useAuth();

  if (!current_cat) return null;
  const breadcrumbItems = getBreadcrumbItems(current_cat);

  function getTitle() {
    if (!current_cat) return '';
    if (searchTitle) return searchTitle;

    const titleAdditions = new Set();

    if (filterState.letterSizes?.length) {
      titleAdditions.add(`Size ${filterState.letterSizes[0]}`);
    }

    if (filterState.bikeYears && filterState.bikeYears.length === 1) {
      titleAdditions.add(filterState.bikeYears[0]);
    }

    if (filterState.bikeMakes && filterState.bikeMakes.length === 1) {
      // titleAdditions.add(capitalize(filterState.bikeMakes[0].toLowerCase()));
      titleAdditions.add(
        filterState.bikeMakes[0].split(' ').map(capitalize).join(' ')
      );
    }

    if (filterState.bikeModels && filterState.bikeModels.length === 1) {
      titleAdditions.add(filterState.bikeModels[0]);
    }

    if (filterState.conditions.length === 1) {
      titleAdditions.add(filterState.conditions[0]);
    }

    if ((filterState.genders ?? []).length === 1) {
      titleAdditions.add(getGenderLabel(filterState.genders?.[0] ?? 'U'));
    }

    if ((filterState.numberSizes ?? []).length === 1) {
      titleAdditions.add(`Size ${filterState.numberSizes[0]}`);
    }

    if (filterState.brands.length === 1) {
      titleAdditions.add(filterState.brands[0]);
    }

    if (filterState.onsale) {
      titleAdditions.add('On Sale');
    }

    let base_title = current_cat.meta_title?.length
      ? current_cat.meta_title?.split(' | ')[0]
      : current_cat.name;

    if (filterState.brands[0] === 'OEM') {
      base_title = base_title.replace('OEM & Aftermarket', '');
    }

    if (filterState.bikeModels) {
      base_title = base_title.replace('Dirt Bike & Motocross', '');
    }

    return titleAdditions.size
      ? `${Array.from(titleAdditions).join(' ')} ${base_title}  ${
          current_cat.name === 'Protective' ? 'Gear' : ''
        }`
      : `${base_title} ${current_cat.name === 'Protective' ? 'Gear' : ''}`;
  }
  return (
    <>
      <div className="relative flex h-fit w-full items-center overflow-clip transition-colors">
        <div className="relative z-10 mx-auto flex min-h-[10rem] w-full flex-col items-center justify-between px-[2.5rem] sm:min-h-[23rem] sm:flex-row sm:px-0">
          <div className="flex w-full flex-col items-start sm:mr-[6.4rem]">
            <div className="mb-2 mt-8 w-full sm:my-8">
              <div className="-ml-3">
                <Breadcrumb
                  items={breadcrumbItems}
                  color="text-brand-secondary"
                />
              </div>
            </div>

            <h1 className="mt-8 text-[2.4rem] font-semibold sm:text-[3.2rem]">
              {getTitle()}
            </h1>

            <div className="hidden sm:flex">
              {current_cat.description && (
                <div
                  className="my-4 text-[12px] text-zinc-500"
                  dangerouslySetInnerHTML={{
                    __html: current_cat.description,
                  }}
                />
              )}
            </div>
          </div>

          {!!userDoc?.roles?.admin && (
            <Button
              text="Edit Category"
              href={`/dashboard/categories/edit/${current_cat.id}`}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default CategoryPageHeader;
